import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { getPickingLists, getPicking, createPicking, extractPicking, togglePickingLine, completePicking, getLibraryMaterials } from '../../services/api';
import { toast } from 'sonner';
import { ClipboardList, Upload, Plus, Trash2, CheckCircle2, Circle, Search, PackageCheck } from 'lucide-react';

const StatusBadge = ({ status }) => {
  const cls = status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700';
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{status === 'completed' ? 'Completed' : 'Open'}</span>;
};

const TypePill = ({ type }) => (
  <span className={`px-2 py-0.5 rounded-full text-xs ${type === 'profile' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>{type || '-'}</span>
);

const PickingTab = () => {
  const [lists, setLists] = useState([]);
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [mode, setMode] = useState('manual'); // 'manual' | 'upload'
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [draftLines, setDraftLines] = useState([]);
  const [libSearch, setLibSearch] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [completing, setCompleting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [pl, lm] = await Promise.all([getPickingLists(), getLibraryMaterials()]);
      setLists(pl.data);
      setLibrary(lm.data);
    } catch (e) {
      toast.error('Failed to load picking lists');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setMode('manual'); setReference(''); setNotes(''); setDraftLines([]); setLibSearch('');
    setCreateOpen(true);
  };

  const addLibItem = (m) => {
    const exists = draftLines.some((l) => l.lib_id === m.id);
    if (exists) { toast.info('Item already added'); return; }
    const isProfile = m.item_type === 'profile';
    setDraftLines([...draftLines, {
      lib_id: m.id,
      item_code: m.item_code || '',
      item_name: m.item_name,
      item_type: m.item_type || 'hardware',
      unit: isProfile ? 'm' : (m.unit || 'pcs'),
      qty_to_pick: 0,
    }]);
  };

  const updateLineQty = (idx, val) => {
    const next = draftLines.slice();
    next[idx] = { ...next[idx], qty_to_pick: val };
    setDraftLines(next);
  };

  const removeLine = (idx) => {
    const next = draftLines.slice();
    next.splice(idx, 1);
    setDraftLines(next);
  };

  const handleUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await extractPicking(file);
      const raw = res.data.line_items || [];
      const items = raw.map((it) => ({
        lib_id: null,
        item_code: it.item_code || '',
        item_name: it.item_name,
        item_type: it.item_type || 'hardware',
        unit: it.unit || 'pcs',
        qty_to_pick: it.qty_to_pick || 0,
      }));
      setDraftLines(items);
      if (items.length === 0) toast.warning('No items could be extracted from the file');
      else toast.success(`Extracted ${items.length} item(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Extraction failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleCreate = async () => {
    const valid = draftLines.filter((l) => l.item_name && Number(l.qty_to_pick) > 0);
    if (valid.length === 0) { toast.error('Add at least one line with a quantity'); return; }
    setSaving(true);
    try {
      const payload = {
        reference: reference || null,
        notes: notes || null,
        line_items: valid.map((l) => ({
          lib_id: l.lib_id || null,
          item_code: l.item_code || '',
          item_name: l.item_name,
          item_type: l.item_type || 'hardware',
          unit: l.unit || 'pcs',
          qty_to_pick: Number(l.qty_to_pick),
        })),
      };
      await createPicking(payload);
      toast.success('Picking list created');
      setCreateOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create picking list');
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (id) => {
    try {
      const res = await getPicking(id);
      setDetail(res.data);
      setDetailOpen(true);
    } catch (e) { toast.error('Failed to open picking list'); }
  };

  const toggleLine = async (idx, picked) => {
    if (detail.status === 'completed') return;
    try {
      await togglePickingLine(detail.id, idx, picked);
      const next = { ...detail, line_items: detail.line_items.slice() };
      next.line_items[idx] = { ...next.line_items[idx], picked };
      setDetail(next);
    } catch (e) { toast.error('Failed to update line'); }
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      const res = await completePicking(detail.id);
      toast.success('Picking completed — stock deducted');
      setDetail(res.data);
      setDetailOpen(false);
      fetchData();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && d.errors) {
        toast.error(d.message || 'Cannot complete picking');
        const msgs = d.errors;
        for (let i = 0; i < msgs.length; i++) { toast.error(msgs[i]); }
      } else {
        toast.error(typeof d === 'string' ? d : 'Failed to complete picking');
      }
    } finally {
      setCompleting(false);
    }
  };

  const filteredLib = library.filter((m) =>
    (m.item_name || '').toLowerCase().includes(libSearch.toLowerCase()) ||
    (m.item_code || '').toLowerCase().includes(libSearch.toLowerCase())
  );
  const libResults = filteredLib.slice(0, 20);

  const detailLines = detail ? detail.line_items : [];
  const tickedCount = detail ? detail.line_items.filter((l) => l.picked).length : 0;

  return (
    <div className="space-y-4" data-testid="picking-tab-content">
      <Card><CardContent className="p-4 flex items-center justify-between">
        <div>
          <p className="font-medium">Fabrication Picking Lists</p>
          <p className="text-sm text-muted-foreground">Pick profiles (in metres) and hardware, then complete to deduct from inventory</p>
        </div>
        <Button className="btn-accent" onClick={openCreate} data-testid="new-picking-btn"><Plus size={16} className="mr-1" />New Picking List</Button>
      </CardContent></Card>

      <Card><CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" /></div>
        ) : lists.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-muted-foreground"><ClipboardList size={40} className="mb-3 opacity-50" /><p>No picking lists yet</p><p className="text-sm mt-1">Create one to start picking material for fabrication</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50"><tr>
                <th className="px-4 py-3 text-left text-sm font-medium">Picking No.</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Reference</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Lines</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Created</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Action</th>
              </tr></thead>
              <tbody className="divide-y">
                {lists.map((pl) => (
                  <tr key={pl.id} className="hover:bg-muted/30" data-testid={`picking-row-${pl.id}`}>
                    <td className="px-4 py-3 font-mono text-sm">{pl.picking_number}</td>
                    <td className="px-4 py-3 text-sm">{pl.reference || '-'}</td>
                    <td className="px-4 py-3 text-sm">{(pl.line_items || []).length}</td>
                    <td className="px-4 py-3"><StatusBadge status={pl.status} /></td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{(pl.created_at || '').slice(0, 10)}</td>
                    <td className="px-4 py-3"><Button variant="outline" size="sm" onClick={() => openDetail(pl.id)} data-testid={`open-picking-${pl.id}`}>{pl.status === 'completed' ? 'View' : 'Pick'}</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent></Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Picking List</DialogTitle>
            <DialogDescription>Build a list manually from the Material Library or upload a file to auto-extract items.</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-muted-foreground">Reference (job/project)</Label><Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. Tower-A Casements" className="mt-1" data-testid="picking-reference" /></div>
            <div><Label className="text-xs text-muted-foreground">Notes</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" className="mt-1" data-testid="picking-notes" /></div>
          </div>

          <div className="flex gap-2 border-b">
            <button className={`px-3 py-2 text-sm border-b-2 ${mode === 'manual' ? 'border-accent font-medium' : 'border-transparent text-muted-foreground'}`} onClick={() => setMode('manual')} data-testid="mode-manual">Manual</button>
            <button className={`px-3 py-2 text-sm border-b-2 ${mode === 'upload' ? 'border-accent font-medium' : 'border-transparent text-muted-foreground'}`} onClick={() => setMode('upload')} data-testid="mode-upload">Upload File</button>
          </div>

          {mode === 'manual' ? (
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                <Input placeholder="Search library items..." value={libSearch} onChange={(e) => setLibSearch(e.target.value)} className="pl-9" data-testid="picking-lib-search" />
              </div>
              {libSearch && (
                <div className="border rounded max-h-40 overflow-y-auto divide-y">
                  {filteredLib.length === 0 ? <p className="text-sm text-muted-foreground p-3">No matches</p> : libResults.map((m) => (
                    <button key={m.id} className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-muted/40 text-left" onClick={() => addLibItem(m)} data-testid={`add-lib-${m.id}`}>
                      <span><span className="font-mono text-xs mr-2">{m.item_code || '-'}</span>{m.item_name}</span>
                      <TypePill type={m.item_type} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div>
              <label className="flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-6 cursor-pointer hover:bg-muted/30" data-testid="picking-upload-label">
                <Upload size={24} className="text-muted-foreground mb-2" />
                <span className="text-sm text-muted-foreground">{uploading ? 'Extracting…' : 'Click to upload CSV, Excel, PDF or image'}</span>
                <input type="file" className="hidden" accept=".pdf,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.webp" onChange={handleUpload} disabled={uploading} data-testid="picking-file-input" />
              </label>
            </div>
          )}

          {draftLines.length > 0 && (
            <div className="border rounded overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted/50"><tr>
                  <th className="px-3 py-2 text-left text-xs font-medium">Code</th>
                  <th className="px-3 py-2 text-left text-xs font-medium">Item</th>
                  <th className="px-3 py-2 text-left text-xs font-medium">Type</th>
                  <th className="px-3 py-2 text-left text-xs font-medium">Qty to Pick</th>
                  <th className="px-3 py-2 text-left text-xs font-medium">UOM</th>
                  <th className="px-3 py-2"></th>
                </tr></thead>
                <tbody className="divide-y">
                  {draftLines.map((l, idx) => (
                    <tr key={idx} data-testid={`draft-line-${idx}`}>
                      <td className="px-3 py-2 font-mono text-xs">{l.item_code || '-'}</td>
                      <td className="px-3 py-2 text-sm">{l.item_name}</td>
                      <td className="px-3 py-2"><TypePill type={l.item_type} /></td>
                      <td className="px-3 py-2"><Input type="number" value={l.qty_to_pick} onChange={(e) => updateLineQty(idx, e.target.value)} className="h-8 w-24" data-testid={`draft-qty-${idx}`} /></td>
                      <td className="px-3 py-2 text-sm">{l.unit}</td>
                      <td className="px-3 py-2"><button onClick={() => removeLine(idx)} className="text-muted-foreground hover:text-red-600" data-testid={`draft-remove-${idx}`}><Trash2 size={14} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <Button className="w-full btn-accent" onClick={handleCreate} disabled={saving || draftLines.length === 0} data-testid="save-picking-btn">{saving ? 'Saving…' : 'Create Picking List'}</Button>
        </DialogContent>
      </Dialog>

      {/* Detail / pick dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">{detail?.picking_number} <StatusBadge status={detail?.status} /></DialogTitle>
            <DialogDescription>{detail?.reference ? `Reference: ${detail.reference}. ` : ''}Tick each line once physically picked, then complete to deduct stock.</DialogDescription>
          </DialogHeader>

          {detail && (
            <div className="border rounded overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted/50"><tr>
                  <th className="px-3 py-2 w-10"></th>
                  <th className="px-3 py-2 text-left text-xs font-medium">Code</th>
                  <th className="px-3 py-2 text-left text-xs font-medium">Item</th>
                  <th className="px-3 py-2 text-left text-xs font-medium">Type</th>
                  <th className="px-3 py-2 text-left text-xs font-medium">Qty to Pick</th>
                  <th className="px-3 py-2 text-left text-xs font-medium">Available</th>
                </tr></thead>
                <tbody className="divide-y">
                  {detailLines.map((l, idx) => {
                    const short = l.available != null && Number(l.qty_to_pick) > Number(l.available);
                    return (
                      <tr key={idx} className={l.picked ? 'bg-green-50/50' : ''} data-testid={`detail-line-${idx}`}>
                        <td className="px-3 py-2">
                          <button disabled={detail.status === 'completed'} onClick={() => toggleLine(idx, !l.picked)} data-testid={`toggle-line-${idx}`}>
                            {l.picked ? <CheckCircle2 size={18} className="text-green-600" /> : <Circle size={18} className="text-muted-foreground" />}
                          </button>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{l.item_code || '-'}</td>
                        <td className="px-3 py-2 text-sm">{l.item_name}</td>
                        <td className="px-3 py-2"><TypePill type={l.item_type} /></td>
                        <td className="px-3 py-2 font-mono text-sm">{l.qty_to_pick} {l.unit}</td>
                        <td className={`px-3 py-2 font-mono text-sm ${l.available == null ? 'text-red-600' : short ? 'text-red-600 font-semibold' : ''}`}>
                          {l.available == null ? 'Not in library' : `${l.available} ${l.available_unit || l.unit}`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {detail && detail.status !== 'completed' && (
            <Button className="w-full btn-accent" onClick={handleComplete} disabled={completing || tickedCount === 0} data-testid="complete-picking-btn">
              <PackageCheck size={16} className="mr-1" />{completing ? 'Completing…' : `Complete Picking (${tickedCount} ticked)`}
            </Button>
          )}
          {detail && detail.status === 'completed' && (
            <p className="text-sm text-green-700 flex items-center gap-2"><CheckCircle2 size={16} />Completed on {(detail.completed_at || '').slice(0, 10)} — stock has been deducted.</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PickingTab;
